import { DealRequestAllocation } from '../../shared/models';
import { ReactComponent as WorkingCapital } from '../../shared/icons/WorkingCapital.svg';
import { DebtCapitalHeading } from './DebtCapitalHeading';
import {
	CerebroComplexListItem,
	CerebroHeading,
	CerebroLabel,
	CerebroStyledBox,
	CerebroText,
} from '../../shared/components';
import { Grid } from '@mui/material';
import { BooleanUtils, NumberUtils } from '../../shared/utils';

interface DebtWorkingCapitalProps {
	allocation: DealRequestAllocation;
}

export const DebtWorkingCapital: React.FC<DebtWorkingCapitalProps> = ({ allocation }) => {
	const isEmpty = isWorkingCapitalEmpty(allocation);

	return (
		<>
			<DebtCapitalHeading icon={WorkingCapital} amountRequested={allocation.totalWorkingCapitalAmount ?? 0}>
				Working Capital
			</DebtCapitalHeading>

			{isEmpty ? (
				<CerebroStyledBox sx={{ marginBottom: 8 }}>
					<CerebroHeading size="h2" component="h2">
						No details have been added yet.
					</CerebroHeading>
					<br />
				</CerebroStyledBox>
			) : (
				<CerebroComplexListItem sx={{ marginTop: 2, marginBottom: 8 }}>
					<Grid container>
						<Grid item xs={6}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
								Do you need to finance your accounts receivable?
							</CerebroLabel>
							<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
								{BooleanUtils.YesNo(allocation.workingCapitalImproveLiquidity)}
							</CerebroText>
						</Grid>
						<Grid item xs={6}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
								Do you need to finance inventory?
							</CerebroLabel>
							<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
								{BooleanUtils.YesNo(allocation.workingCapitalPurchaseInventory)}
							</CerebroText>
						</Grid>
						<Grid item xs={6}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
								Do you need capital for operating expenses (SG&amp;A)?
							</CerebroLabel>
							<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
								{BooleanUtils.YesNo(allocation.workingCapitalOperatingExpenses)}
							</CerebroText>
						</Grid>
						{allocation.acquiringBusiness == true && (
							<Grid item xs={6}>
								<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
									Will any of this financing be applied to the purchase price of the acquisition?
								</CerebroLabel>
								<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
									{BooleanUtils.YesNo(allocation.workingCapitalForAcquisition)}
								</CerebroText>
							</Grid>
						)}
						{allocation.acquiringBusiness == true && allocation.workingCapitalForAcquisition == true && (
							<Grid item xs={6}>
								<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
									Amount applied to acquisition
								</CerebroLabel>
								<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
									{NumberUtils.FormatCurrency(allocation.workingCapitalForAcquisitionAmount)}
								</CerebroText>
							</Grid>
						)}
					</Grid>
				</CerebroComplexListItem>
			)}
		</>
	);
};

export function isWorkingCapitalEmpty(allocation: DealRequestAllocation): boolean {
	return (
		!allocation.totalWorkingCapitalAmount &&
		!(allocation.workingCapitalImproveLiquidity ?? false) &&
		!(allocation.workingCapitalPurchaseInventory ?? false) &&
		!(allocation.workingCapitalOperatingExpenses ?? false)
	);
}
